import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminUserInfo } from '../../business/auth/AdminUserManager';
import AppLoadingScreen from '../shared/AppLoadingScreen';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: keyof AdminUserInfo['permissions'];
  requireHostAdmin?: boolean;
  requireTenantAdmin?: boolean;
  requirePartner?: boolean;
  requirePartnerOwner?: boolean;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
  children,
  requiredPermission,
  requireHostAdmin = false,
  requireTenantAdmin = false,
  requirePartner = false,
  requirePartnerOwner = false,
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isHostAdmin, isTenantAdmin, isPartner, isPartnerOwner, isAnyAdmin, hasPermission, isLoading: adminLoading } = useAdmin();

  // Show loading while checking authentication and admin status
  if (authLoading || adminLoading) {
    return <AppLoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has any admin privileges
  if (!isAnyAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-ink-900 mb-2">Access Denied</h1>
          <p className="text-ink-600 mb-4">You don't have permission to access the administration area.</p>
          <button 
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check partner requirement
  if (requirePartner && !isPartner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-ink-900 mb-2">Partner Access Required</h1>
          <p className="text-ink-600 mb-4">This feature requires partner privileges.</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check partner-owner requirement (Owner-only partner pages, e.g. Billing).
  // Backend enforces the real boundary; this stops a Member from loading the page.
  if (requirePartnerOwner && !(isPartner && isPartnerOwner)) {
    return <Navigate to="/admin/partner-portal" replace />;
  }

  // Check host admin requirement
  if (requireHostAdmin && !isHostAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-ink-900 mb-2">Host Admin Required</h1>
          <p className="text-ink-600 mb-4">This feature requires host administrator privileges.</p>
          <button 
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check tenant admin requirement
  if (requireTenantAdmin && !isTenantAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-ink-900 mb-2">Tenant Admin Required</h1>
          <p className="text-ink-600 mb-4">This feature requires tenant administrator privileges.</p>
          <button 
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check specific permission requirement
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-ink-900 mb-2">Permission Required</h1>
          <p className="text-ink-600 mb-4">You don't have the required permission to access this feature.</p>
          <button 
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;